import { FunctionComponent } from 'react';
import { useLocation } from 'react-router-dom';
import EmailSenderComponent from './EmailSenderComponent';
import EmailSenderPreview, { EmailSenderPreviewName } from './EmailSenderPreview';

/**
 * Standalone wrapper used by the Email Marketing module's tab system. Today
 * it's a thin pass-through to the core component — we intentionally keep the
 * indirection because:
 *
 *   - The host owns the *context* (tab vs modal), so any tab-specific chrome
 *     (page heading, breadcrumbs, max-width container, etc.) can live here
 *     later without touching the core or the embedded usage.
 *   - The core component takes optional `onVerified` / `onExit` callbacks for
 *     Phase 2's modal mode. In standalone mode the user navigates via tabs,
 *     so the host omits both — no callbacks means no side-effects on state
 *     transitions, which is exactly what we want when the component lives on
 *     a permanent settings page.
 *
 * TEMP — preview mode for visual review. Pass `?preview=<state>` in the URL
 * to render a mocked state with synthetic data, bypassing the slice + BE.
 * Valid states: `unconfigured`, `pending`, `verified`, `failed`, `setup-modal`,
 * `edit-modal`, `escape-modal`. Remove the preview branch (and
 * `EmailSenderPreview.tsx`) before shipping.
 */
const VALID_PREVIEWS: EmailSenderPreviewName[] = [
    'unconfigured',
    'pending',
    'verified',
    'failed',
    'setup-modal',
    'edit-modal',
    'escape-modal'
];

const EmailSenderHost: FunctionComponent = () => {
    const location = useLocation();
    const previewParam = new URLSearchParams(location.search).get('preview');
    const preview = VALID_PREVIEWS.find(name => name === previewParam);

    if (preview) {
        return <EmailSenderPreview preview={preview} />;
    }

    return <EmailSenderComponent />;
};

export default EmailSenderHost;
